<template>
  <div>
    <!-- Floating Button (only when buttonStyle is 'floating') -->
    <div v-if="!isOpen && buttonStyle === 'floating'" class="floating-button" @click="openChat">
      <img :src="magicIcon" alt="AI" class="button-icon" />
      <div class="pulse-ring"></div>
    </div>

    <!-- Chat Window -->
    <transition name="slide-up">
      <div v-if="isOpen" class="chat-window">
        <!-- Header -->
        <div class="chat-header">
          <div class="header-left">
            <div class="header-icon">
              <img :src="magicIcon" alt="AI" class="icon-image" />
            </div>
            <div class="header-text">
              <div class="header-title">{{ t('chat.ai_assistant') }}</div>
              <div class="header-status">{{ t('chat.new_document_welcome_message') }}</div>
            </div>
          </div>
          <button class="close-button" @click="closeChat">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>

        <!-- Real Chat Component -->
        <div class="chat-content-wrapper">
          <Chat
            :initial-messages="initialMessages"
            :currentDocument="currentDocument"
            :minimized="false"
            class="embedded-chat"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import Chat from "@/components/chat/Chat.vue";

import magicIcon from "@/assets/icons/magic-blue-icon.png";
import { ref, computed, watch, readonly, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  currentDocument: {
    type: Object,
    default: null,
  },
  buttonStyle: {
    type: String,
    default: 'floating', // 'floating' or 'external'
    validator: (value) => ['floating', 'external'].includes(value),
  },
});

const emit = defineEmits(["update:modelValue", "template-selected"]);

const isOpen = ref(false);

// Initial messages for the chat (same as old NewDocument)
const initialMessages = computed(() => [
  {
    id: crypto.randomUUID(),
    source: "bot",
    message: t("chat.new_document_welcome_message"),
  },
]);

const openChat = () => {
  isOpen.value = true;
  emit("update:modelValue", true);

  // Prevent body scroll on mobile when chat is open
  if (window.innerWidth <= 600) {
    document.body.style.overflow = 'hidden';
  }
};

const closeChat = () => {
  isOpen.value = false;
  emit("update:modelValue", false);

  // Restore body scroll when chat is closed
  document.body.style.overflow = '';
};

// Watch for external model changes
watch(
  () => props.modelValue,
  (newValue) => {
    const wasOpen = isOpen.value;
    isOpen.value = newValue;

    // Handle body scroll when controlled externally
    if (newValue && !wasOpen && window.innerWidth <= 600) {
      document.body.style.overflow = 'hidden';
    } else if (!newValue && wasOpen) {
      document.body.style.overflow = '';
    }
  },
);

// Cleanup on component unmount
onUnmounted(() => {
  // Restore body scroll if component is unmounted while chat is open
  document.body.style.overflow = '';
});

// Expose methods for external control
defineExpose({
  openChat,
  closeChat,
  isOpen: readonly(isOpen),
});
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600&display=swap");

.floating-button {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  padding: 1rem;
  background: linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  font-family: "Archivo", sans-serif;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 24px rgba(59, 130, 246, 0.4);
  }

  .button-icon {
    width: 28px;
    height: 28px;
    filter: brightness(0) invert(1); // Convert blue icon to white
    opacity: 0.95;
  }

  .pulse-ring {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 2px solid rgba(59, 130, 246, 0.5);
    transform: translate(-50%, -50%);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
    100% {
      transform: translate(-50%, -50%) scale(1.3);
      opacity: 0;
    }
  }

  @media (max-width: 768px) {
    // Move up on tablets and mobile to avoid navigation bar
    bottom: 6rem;
    right: 1rem;
  }

  @media (max-width: 600px) {
    bottom: 5rem;
    right: 1rem;
    width: 56px;
    height: 56px;
    padding: 0.75rem;

    .button-icon {
      width: 24px;
      height: 24px;
    }
  }
}

.chat-window {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 400px;
  height: 600px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 35, 239, 0.15);
  border: 1px solid rgba(0, 35, 239, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 1001;
  overflow: hidden;
  font-family: "Archivo", sans-serif;

  @media (max-width: 600px) {
    bottom: 0;
    right: 0;
    left: 0;
    top: 0;
    width: 100%;
    height: 100vh;
    border-radius: 0;
    border: none;
    position: fixed;
    z-index: 9999;
  }
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #0023ef 0%, #0019cc 100%);
  color: white;
  position: relative;
  overflow: hidden;

  // Add shiny animation effect
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.15),
      transparent
    );
    animation: shine 3s ease-in-out infinite;
    animation-delay: 0.5s;
  }

  @keyframes shine {
    0% {
      left: -100%;
    }
    50% {
      left: 100%;
    }
    100% {
      left: 100%;
    }
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .icon-image {
      width: 24px;
      height: 24px;
      filter: brightness(0) invert(1); // Convert blue icon to white
      opacity: 0.95;
    }
  }

  .header-text {
    .header-title {
      font-size: 18px;
      font-weight: 700;
      margin-bottom: 4px;
      line-height: 1.2;
    }

    .header-status {
      font-size: 12px;
      opacity: 0.9;
      line-height: 1.3;
    }
  }

  .close-button {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: all 0.2s ease;
    width: 36px;
    height: 36px;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: scale(1.05);
    }
  }
}

.chat-content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;

  // Hide all scrollbars on mobile completely
  @media (max-width: 496px) {
    scrollbar-width: none !important; // Firefox
    -ms-overflow-style: none !important; // IE and Edge

    &::-webkit-scrollbar {
      display: none !important; // Chrome, Safari, Opera
      width: 0 !important;
      height: 0 !important;
    }

    // Ensure no scrolling issues on any child elements
    * {
      scrollbar-width: none !important;
      -ms-overflow-style: none !important;

      &::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
    }
  }

  .embedded-chat {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    background: white;
    padding-bottom: 0 !important;

    // Override the chat component's default styling to fit in the popup
    :deep(.chat-container) {
      height: 100% !important;
      border: none !important;
      border-radius: 0 !important;
      background: transparent !important;

      // Hide the duplicate chat header since we have our own
      .chat-header {
        display: none !important;
      }

      // Also target it as direct child
      > .chat-header {
        display: none !important;
      }

      // Hide any content container that might have unwanted elements
      .content-container {
        > *:not(.content):not(.message-input-container) {
          display: none !important;
        }
      }
    }

    // Force hide the chat header with maximum specificity
    :deep(div.chat-header) {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      overflow: hidden !important;
    }

    :deep(.chat-content) {
      flex: 1 !important;
      height: auto !important;
      background: transparent !important;

      // Hide any content that might be showing before the main messages
      > *:first-child {
        display: none !important;
      }
    }

    :deep(.chat-messages) {
      background: white !important;
      padding: 1.5rem 1.5rem 0 1.5rem !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;

      // Ensure all message elements wrap properly
      * {
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        white-space: pre-wrap !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }

      // Specific targeting for message containers
      .message-item,
      .message-bubble,
      .message-text,
      .message,
      div[class*="message"] {
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        white-space: pre-wrap !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }

      &::-webkit-scrollbar {
        width: 6px !important;
      }

      &::-webkit-scrollbar-track {
        background: transparent !important;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(0, 35, 239, 0.2) !important;
        border-radius: 10px !important;
      }

      // Hide scrollbar on mobile completely
      @media (max-width: 496px) {
        scrollbar-width: none !important; // Firefox
        -ms-overflow-style: none !important; // IE and Edge

        &::-webkit-scrollbar {
          display: none !important; // Chrome, Safari, Opera
          width: 0 !important;
          height: 0 !important;
        }

        &::-webkit-scrollbar-track {
          display: none !important;
        }

        &::-webkit-scrollbar-thumb {
          display: none !important;
        }
      }
    }

    // Fix horizontal scroll in infinite scroll component
    :deep(.v-infinite-scroll) {
      overflow-x: hidden !important;
      overflow-y: auto !important;

      // Hide scrollbar on mobile completely
      @media (max-width: 496px) {
        scrollbar-width: none !important; // Firefox
        -ms-overflow-style: none !important; // IE and Edge

        &::-webkit-scrollbar {
          display: none !important; // Chrome, Safari, Opera
          width: 0 !important;
          height: 0 !important;
        }

        &::-webkit-scrollbar-track {
          display: none !important;
        }

        &::-webkit-scrollbar-thumb {
          display: none !important;
        }
      }
    }

    // Style message bubbles to match your theme
    :deep(.message-content) {
      .user-message {
        background: linear-gradient(135deg, #0023ef 0%, #0019cc 100%) !important;
        border-radius: 16px 16px 4px 16px !important;
        color: white !important;
        box-shadow: 0 2px 8px rgba(0, 35, 239, 0.2) !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        white-space: pre-wrap !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }

      .bot-message {
        background: white !important;
        border: 1px solid #e2e8f0 !important;
        border-radius: 16px 16px 16px 4px !important;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
        word-wrap: break-word !important;
        overflow-wrap: break-word !important;
        white-space: pre-wrap !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
    }

    // Style the message input container
    :deep(.message-input-container) {
      padding: 0.75rem 1.5rem 0 1.5rem !important;
      background: white !important;
      border-top: 1px solid #e2e8f0 !important;
    }

    // Style the message container (direct parent of textarea)
    :deep(.message-container) {
      .v-textarea {
        .v-field {
          border-radius: 16px !important;
          border: 1px solid #e2e8f0 !important;
          background: #fafafa !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02) !important;
          transition: all 0.2s ease !important;

          &:hover {
            border-color: #cbd5e1 !important;
            background: #f8fafc !important;
          }

          &.v-field--focused {
            border-color: #0023ef !important;
            background: white !important;
            box-shadow: 0 0 0 3px rgba(0, 35, 239, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05) !important;
          }

          .v-field__input {
            padding: 12px 16px !important;
            font-family: "Archivo", sans-serif !important;
            font-size: 14px !important;
            line-height: 1.4 !important;
            color: #334155 !important;

            &::placeholder {
              color: #94a3b8 !important;
              opacity: 1 !important;
            }
          }

          .v-field__append-inner {
            padding-right: 12px !important;

            .message-button {
              width: 32px !important;
              height: 32px !important;
              padding: 6px !important;
              background: white !important;
              border-radius: 12px !important;
              transition: all 0.2s ease !important;
              cursor: pointer !important;
              border: 2px solid #0023ef !important;

              &:hover {
                transform: scale(1.05) !important;
                box-shadow: 0 4px 12px rgba(0, 35, 239, 0.3) !important;
                background: #f8fafc !important;
              }

              &:active {
                transform: scale(0.98) !important;
              }
            }
          }

          .v-field__outline {
            display: none !important;
          }
        }
      }
    }

    // Override disclaimer styling to match blue theme and hide redundant text
    :deep(.disclaimer) {
      .title {
        display: none !important; // Hide "Aby skorzystać z Asystenta Prawnego AI..." text
      }
    }

    // Hide any text or element that looks like "Asystent Prawny AI" header
    :deep(.chat-messages) {
      // Hide message headers that might contain this text
      .message-header,
      .ai-header,
      .bot-header,
      .assistant-header {
        display: none !important;
      }

      // Hide any divs with specific patterns that might show this text
      div[class*="asystent"],
      div[class*="assistant"],
      div[class*="ai"],
      .chat-assistant,
      .ai-assistant {
        display: none !important;
      }
    }

    // Additional targeting for the gray bar that contains "Asystent Prawny AI"
    :deep(.content-container) {
      // Hide any element that contains this specific text
      > *:has-text("Asystent Prawny AI"),
      > div:contains("Asystent Prawny AI"),
      .disclaimer .title,
      .chat-title-container,
      .assistant-title {
        display: none !important;
      }
    }

    // Target disclaimer component specifically to hide the gray bar title
    :deep(.disclaimer) {
      .title {
        display: none !important; // This hides "Aby skorzystać z Asystenta Prawnego AI..." text
      }

      .v-alert {
        display: none !important;
      }
    }

    .accept-button {
      background: linear-gradient(135deg, #0023ef 0%, #0019cc 100%) !important;
      border-radius: 12px !important;
      font-weight: 600 !important;
      transition: all 0.2s ease !important;
      text-transform: none !important;
      box-shadow: 0 2px 8px rgba(0, 35, 239, 0.2) !important;
      white-space: normal !important;
      word-break: break-word !important;
      line-height: 1.4 !important;
      min-height: 44px !important;
      height: auto !important;
      padding: 12px 16px !important;

      &:hover {
        background: linear-gradient(135deg, #0019cc 0%, #0014a8 100%) !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 4px 12px rgba(0, 35, 239, 0.3) !important;
      }
    }
  }

  // Override any other buttons in chat to use blue theme
  :deep(.v-btn) {
    &.v-btn--variant-flat {
      background: linear-gradient(135deg, #0023ef 0%, #0019cc 100%) !important;
      color: white !important;
      border-radius: 12px !important;
      font-weight: 600 !important;
      text-transform: none !important;
      box-shadow: 0 2px 8px rgba(0, 35, 239, 0.2) !important;
      white-space: normal !important;
      word-break: break-word !important;
      line-height: 1.4 !important;
      min-height: 44px !important;
      height: auto !important;
      padding: 12px 16px !important;
      overflow-wrap: break-word !important;

      &:hover {
        background: linear-gradient(135deg, #0019cc 0%, #0014a8 100%) !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 4px 12px rgba(0, 35, 239, 0.3) !important;
      }
    }
  }

  // Style for message buttons (sample messages and recommended buttons)
  :deep(.sample-messages .message) {
    border-radius: 12px !important;
    white-space: normal !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    text-wrap: wrap !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
  transform: translateY(20px);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
